/**
 * @fileoverview Creator Dashboard API Client
 * Provides TypeScript API client with authentication token management and automatic retry logic
 */

import {
  UserProfileResponse,
  CreateUserProfileRequest,
  CreatorProfileResponse,
  CreateCreatorProfileRequest,
  ContentPlanResponse,
  ContentRecommendationResponse,
  UpdateRecommendationStatusRequest,
  CreatorDashboardError,
  ApiResponse,
} from '../types/shared';

export interface ApiClientConfig {
  baseUrl: string;
  getAuthToken: () => Promise<string | null>;
  onAuthError?: () => void;
  retryAttempts?: number;
  retryDelay?: number;
  cacheTimeout?: number;
}

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  expiresAt: number;
}

export class CreatorDashboardApiClient {
  private config: Required<ApiClientConfig>;
  private cache = new Map<string, CacheEntry<any>>();

  constructor(config: ApiClientConfig) {
    this.config = {
      retryAttempts: 3,
      retryDelay: 1000,
      cacheTimeout: 5 * 60 * 1000, // 5 minutes
      onAuthError: () => { },
      ...config,
    };
  }

  /**
   * Make authenticated API request with retry logic
   */
  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {},
    useCache = false
  ): Promise<T> {
    const cacheKey = `${endpoint}:${JSON.stringify(options)}`;

    // Check cache first if enabled
    if (useCache && options.method === 'GET') {
      const cached = this.getCachedData<T>(cacheKey);
      if (cached) {
        return cached;
      }
    }

    let lastError: Error | null = null;

    for (let attempt = 0; attempt <= this.config.retryAttempts; attempt++) {
      try {
        const token = await this.config.getAuthToken();
        if (!token) {
          throw new Error('No authentication token available');
        }

        const response = await fetch(`${this.config.baseUrl}${endpoint}`, {
          ...options,
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            ...options.headers,
          },
        });

        if (response.status === 401 || response.status === 403) {
          this.config.onAuthError();
          throw new Error('Authentication failed');
        }

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          const error = new Error(errorData.error?.message || `HTTP ${response.status}: ${response.statusText}`);
          // Preserve error code from API response
          if (errorData.error?.code) {
            (error as any).code = errorData.error.code;
          }
          // Preserve error details
          if (errorData.error?.details) {
            (error as any).details = errorData.error.details;
          }
          // Preserve HTTP status
          (error as any).status = response.status;
          throw error;
        }

        const result: ApiResponse<T> = await response.json();

        if (!result.success || !result.data) {
          throw new Error(result.error?.message || 'API request failed');
        }

        // Cache successful GET requests
        if (useCache && options.method === 'GET') {
          this.setCachedData(cacheKey, result.data);
        }

        return result.data;
      } catch (error) {
        lastError = error instanceof Error ? error : new Error('Unknown error');

        // Don't retry on authentication errors
        if (lastError.message.includes('Authentication failed')) {
          throw lastError;
        }

        // Don't retry on client errors (4xx) - these are business logic errors
        const status = (lastError as any).status;
        if (status && status >= 400 && status < 500) {
          throw lastError;
        }

        // Don't retry on specific error codes
        const errorCode = (lastError as any).code;
        if (errorCode === 'MAX_VERSIONS_REACHED' || 
            errorCode === 'INSUFFICIENT_TREND_DATA') {
          throw lastError;
        }

        // Don't retry on business logic errors or API request failures
        if (lastError.message.includes('Business logic error') ||
          lastError.message.includes('API request failed') ||
          lastError.message.includes('Invalid request')) {
          throw lastError;
        }

        // Don't retry on timeouts (504) or timeout-related errors
        // Retrying timeouts for generation operations causes duplicate plans/recommendations
        if (lastError.message.includes('HTTP 504') ||
          lastError.message.includes('timeout') ||
          lastError.message.includes('timed out') ||
          lastError.message.toLowerCase().includes('gateway timeout')) {
          throw lastError;
        }

        // Wait before retry (exponential backoff)
        if (attempt < this.config.retryAttempts) {
          await this.delay(this.config.retryDelay * Math.pow(2, attempt));
        }
      }
    }

    throw lastError || new Error('Request failed after all retry attempts');
  }

  /**
   * Utility method for delays
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Get cached data if valid
   */
  private getCachedData<T>(key: string): T | null {
    const entry = this.cache.get(key);
    if (entry && Date.now() < entry.expiresAt) {
      return entry.data;
    }

    // Remove expired entry
    if (entry) {
      this.cache.delete(key);
    }

    return null;
  }

  /**
   * Set cached data with expiration
   */
  private setCachedData<T>(key: string, data: T): void {
    const now = Date.now();
    this.cache.set(key, {
      data,
      timestamp: now,
      expiresAt: now + this.config.cacheTimeout,
    });
  }

  /**
   * Clear all cached data
   */
  public clearCache(): void {
    this.cache.clear();
  }

  /**
   * Clear specific cache entry
   */
  public clearCacheEntry(pattern: string): void {
    for (const key of this.cache.keys()) {
      if (key.includes(pattern)) {
        this.cache.delete(key);
      }
    }
  }

  // ============================================================================
  // User Profile API Methods
  // ============================================================================

  /**
   * Get current user profile
   */
  async getUserProfile(): Promise<UserProfileResponse> {
    return this.makeRequest<UserProfileResponse>('/v1/users/profile', { method: 'GET' }, true);
  }

  /**
   * Create or update user profile
   */
  async createUserProfile(profile: CreateUserProfileRequest): Promise<UserProfileResponse> {
    const result = await this.makeRequest<UserProfileResponse>('/v1/users/profile', {
      method: 'POST',
      body: JSON.stringify(profile),
    });

    // Clear user profile cache
    this.clearCacheEntry('/v1/users/profile');

    return result;
  }

  // ============================================================================
  // Creator Profile API Methods
  // ============================================================================

  /**
   * Check if user has a creator profile
   * Used for onboarding flow to determine if user needs to complete profile setup
   */
  async checkCreatorProfile(): Promise<{ hasProfile: boolean; profileCount: number; redirectTo: 'onboarding' | 'dashboard' }> {
    return this.makeRequest<{ hasProfile: boolean; profileCount: number; redirectTo: 'onboarding' | 'dashboard' }>(
      `/v1/creators/profile/check`,
      { method: 'GET' },
      false // Don't cache profile check - we want fresh data
    );
  }

  /**
   * Get creator profile for current user
   */
  async getCreatorProfile(): Promise<CreatorProfileResponse> {
    return this.makeRequest<CreatorProfileResponse>('/v1/creators/profile', { method: 'GET' }, true);
  }

  /**
   * Create or update creator profile
   */
  async createCreatorProfile(profile: CreateCreatorProfileRequest): Promise<CreatorProfileResponse> {
    const result = await this.makeRequest<CreatorProfileResponse>('/v1/creators/profile', {
      method: 'POST',
      body: JSON.stringify(profile),
    });

    // Clear creator profile cache
    this.clearCacheEntry('/v1/creators/profile');

    return result;
  }

  /**
   * Update an existing creator profile
   */
  async updateCreatorProfile(creatorId: string, updates: Partial<CreateCreatorProfileRequest>): Promise<CreatorProfileResponse> {
    const result = await this.makeRequest<CreatorProfileResponse>(`/v1/creators/${creatorId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });

    // Clear creator profile cache
    this.clearCacheEntry('/v1/creators/profile');

    return result;
  }

  // ============================================================================
  // Content Plan API Methods
  // ============================================================================

  /**
   * Get content plans for current creator
   */
  async getContentPlans(limit?: number, offset?: number): Promise<ContentPlanResponse[]> {
    const params = new URLSearchParams();
    if (limit) params.append('limit', limit.toString());
    if (offset) params.append('offset', offset.toString()); // Add offset support

    const queryString = params.toString() ? `?${params.toString()}` : '';
    const response = await this.makeRequest<{ plans: ContentPlanResponse[] }>(`/v1/content-plans${queryString}`, { method: 'GET' }, true);
    return response.plans;
  }

  /**
   * Get content plan for a specific week
   * @param weekStartDate - Week start date in YYYY-MM-DD format (Monday)
   * @param platform - Platform (e.g., 'tiktok', 'instagram')
   * @returns Content plan with recommendations, or null if no plan exists for that week
   */
  async getContentPlanByWeek(weekStartDate: string, platform: string): Promise<ContentPlanResponse | null> {
    const params = new URLSearchParams();
    params.append('weekStart', weekStartDate);
    params.append('platform', platform);

    const response = await this.makeRequest<{ plan: ContentPlanResponse | null }>(
      `/v1/content-plans?${params.toString()}`, 
      { method: 'GET' }, 
      true
    );
    return response.plan;
  }

  /**
   * Get specific content plan with recommendations
   */
  async getContentPlan(planId: string): Promise<ContentPlanResponse> {
    return this.makeRequest<ContentPlanResponse>(`/v1/content-plans/${planId}`, { method: 'GET' }, true);
  }

  /**
   * Get current active content plan with recommendations
   */
  async getCurrentContentPlan(): Promise<ContentPlanResponse | null> {
    // Get all plans (no limit) to find the active one
    const plans = await this.getContentPlans();
    if (plans.length === 0) return null;

    // Find the most recent active plan (plans are already filtered for is_active !== false)
    // Sort by start date descending to get the most recent week
    const sortedPlans = plans.sort((a, b) =>
      new Date(b.startDate).getTime() - new Date(a.startDate).getTime()
    );

    const mostRecentPlan = sortedPlans[0];
    if (!mostRecentPlan || !mostRecentPlan.planId) return null;

    // Get the full plan with recommendations
    return this.getContentPlan(mostRecentPlan.planId);
  }

  /**
   * Generate a new content plan
   * Note: This operation should never retry as it may create duplicate plans
   */
  async generateContentPlan(options?: {
    platform?: string;
    weekStartDate?: string;
    reason?: string;
    force?: boolean;
  }): Promise<{ planId: string; status: string; message: string; weekStartDate?: string; platform?: string; startedAt?: string }> {
    // Save original retry config
    const originalRetryAttempts = this.config.retryAttempts;
    
    try {
      // Disable retries for generation to prevent duplicate plans
      this.config.retryAttempts = 0;
      
      const result = await this.makeRequest<{
        planId: string;
        status: string;
        message: string;
        weekStartDate?: string;
        platform?: string;
        startedAt?: string;
      }>(
        '/v1/content-plans/generate',
        {
          method: 'POST',
          body: JSON.stringify(options || {}),
        }
      );

      // Clear all content-related caches since new plan was generated
      this.clearCacheEntry('/v1/content-plans');
      this.clearCacheEntry('/v1/content-recommendations');

      return result;
    } finally {
      // Restore original retry config
      this.config.retryAttempts = originalRetryAttempts;
    }
  }

  // ============================================================================
  // Content Recommendation API Methods
  // ============================================================================

  /**
   * Update recommendation status
   */
  async updateRecommendationStatus(
    recommendationId: string,
    statusUpdate: UpdateRecommendationStatusRequest
  ): Promise<ContentRecommendationResponse> {
    const result = await this.makeRequest<ContentRecommendationResponse>(
      `/v1/content-recommendations/${recommendationId}/status`,
      {
        method: 'PUT',
        body: JSON.stringify(statusUpdate),
      }
    );

    // Clear all content-related caches since recommendations changed - this affects content plans
    this.clearCacheEntry('/v1/content-plans');
    this.clearCacheEntry('/v1/content-recommendations');

    return result;
  }

  async updateRecommendationHelpfulness(
    recommendationId: string,
    helpfulnessRating: 'helpful' | 'not_helpful' | null
  ): Promise<ContentRecommendationResponse> {
    const result = await this.makeRequest<ContentRecommendationResponse>(
      `/v1/content-recommendations/${recommendationId}/helpfulness`,
      {
        method: 'PUT',
        body: JSON.stringify({ helpfulnessRating }),
      }
    );

    this.clearCacheEntry('/v1/content-plans');
    this.clearCacheEntry('/v1/content-recommendations');

    return result;
  }

  async updateRecommendationPerformance(
    recommendationId: string,
    performanceRating: 'better' | 'average' | 'worse' | null
  ): Promise<ContentRecommendationResponse> {
    const result = await this.makeRequest<ContentRecommendationResponse>(
      `/v1/content-recommendations/${recommendationId}/performance`,
      {
        method: 'PUT',
        body: JSON.stringify({ performanceRating }),
      }
    );

    this.clearCacheEntry('/v1/content-plans');
    this.clearCacheEntry('/v1/content-recommendations');

    return result;
  }

  // ============================================================================
  // Version Management API Methods
  // ============================================================================

  /**
   * Get all versions of a content plan
   */
  async getContentPlanVersions(planId: string): Promise<any[]> {
    const response = await this.makeRequest<{ versions: any[] }>(
      `/v1/content-plans/${planId}/versions`,
      { method: 'GET' }
    );
    return response.versions;
  }

  /**
   * Revert content plan to a previous version
   */
  async revertContentPlan(planId: string, targetPlanId: string): Promise<void> {
    await this.makeRequest<{ success: boolean; message: string }>(
      `/v1/content-plans/${planId}/revert`,
      {
        method: 'POST',
        body: JSON.stringify({ targetPlanId }),
      }
    );

    // Clear all content-related caches since plan changed
    this.clearCacheEntry('/v1/content-plans');
    this.clearCacheEntry('/v1/content-recommendations');
  }

  /**
   * Get all versions of a content recommendation
   */
  async getRecommendationVersions(recId: string): Promise<any[]> {
    const response = await this.makeRequest<{ versions: any[] }>(
      `/v1/content-recommendations/${recId}/versions`,
      { method: 'GET' }
    );
    return response.versions;
  }

  /**
   * Revert recommendation to a previous version
   */
  async revertRecommendation(recId: string, targetRecId: string): Promise<void> {
    await this.makeRequest<{ success: boolean; message: string }>(
      `/v1/content-recommendations/${recId}/revert`,
      {
        method: 'POST',
        body: JSON.stringify({ targetRecId }),
      }
    );

    // Clear all content-related caches since recommendation changed
    this.clearCacheEntry('/v1/content-plans');
    this.clearCacheEntry('/v1/content-recommendations');
  }

  /**
   * Regenerate a single recommendation
   * Note: This operation should never retry as it may create duplicate recommendations
   */
  async regenerateRecommendation(recId: string): Promise<void> {
    // Save original retry config
    const originalRetryAttempts = this.config.retryAttempts;
    
    try {
      // Disable retries for regeneration to prevent duplicate recommendations
      this.config.retryAttempts = 0;
      
      await this.makeRequest<{ success: boolean; message: string }>(
        `/v1/content-recommendations/${recId}/regenerate`,
        {
          method: 'POST',
          body: JSON.stringify({}),
        }
      );

      // Clear all content-related caches since recommendation will be regenerated
      this.clearCacheEntry('/v1/content-plans');
      this.clearCacheEntry('/v1/content-recommendations');
    } finally {
      // Restore original retry config
      this.config.retryAttempts = originalRetryAttempts;
    }
  }

  // ============================================================================
  // Utility Methods
  // ============================================================================

  /**
   * Check if API is healthy
   */
  async healthCheck(): Promise<boolean> {
    try {
      await this.makeRequest<{ status: string }>('/v1/health', { method: 'GET' });
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Get cache statistics
   */
  getCacheStats(): { size: number; entries: string[] } {
    return {
      size: this.cache.size,
      entries: Array.from(this.cache.keys()),
    };
  }
}

/**
 * Factory function to create API client instance
 */
export function createCreatorDashboardApiClient(config: ApiClientConfig): CreatorDashboardApiClient {
  return new CreatorDashboardApiClient(config);
}